import { Box } from '@material-ui/core';
import { accessHistoryApi } from 'api/authApi';
import ScreenLoader from 'components/organismos/ScreenLoader';
import moment from 'moment';
import useAppSnackbar from 'hooks/useAppSnackbar';
import handleError from 'utility/handleError';
import SectionHeader from 'pages/reservationBookingOnlineV2/components/SectionHeader';
import { StatusBooking, useReservationBookingOnlineContext } from 'pages/reservationBookingOnlineV2/context';
import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { returnHistoryPath } from 'pages/reservationBookingOnlineV2/functions';
import useStyles from './styles';

interface IReceptionCompleted {
  companyCode:string,
  courseCode?:string,
  mediaCode?:string,
  gender?:string,
  serviceCode?:string,
  trackingCode?:string,
}

const ReceptionCompleted : React.FC<IReceptionCompleted> = ({
  companyCode,
  courseCode,
  mediaCode,
  gender,
  serviceCode,
  trackingCode,
}) => {
  const [isLoading, setLoading] = useState(false);
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { enqueueSnackbar } = useAppSnackbar();
  const timeOutRef = useRef<any>(null);
  const {
    status, customerNumberBooking, contractCourseNameBooking, orgNameBooking, birthDateBooking, emailBooking,
    reservationIdBooking, chosenCourseAcceptRequestReservation, handleResetContext, transactionIdBooking,
    chosenStore,
  } = useReservationBookingOnlineContext();

  const handleBackHome = () => {
    handleResetContext && handleResetContext();
    history.replace({
      search: returnHistoryPath(companyCode, 0, courseCode, mediaCode, gender, serviceCode, trackingCode),
    });
  };

  const updateSize = () => {
    if (timeOutRef.current) {
      clearTimeout(timeOutRef.current);
    }
    timeOutRef.current = setTimeout(() => {
      const height = document.getElementsByTagName('body')[0]?.clientHeight || 0;
      const obj = { event: 'iframe_height', height };
      // 親へiframeの高さを送信
      // postMessage(<送信する値>, <送信先のドメイン>)
      window.parent.postMessage(obj, 'https://www.ci-z.com');
    }, 500);
  };

  // 予約が成立したうえで表示された完了画面かどうか。
  // 予約IDが無い場合（完了画面への直接アクセス・リロード・セッション切れ）は予約が成立していない。
  // status が未確定（CAN_NOT_BOOKING）の場合は予約種別を判定できず問い合わせ側に倒れてしまう。
  // いずれも計測イベントを送ってはいけない。
  const isCompletedReservation = Boolean(reservationIdBooking)
    && (status === StatusBooking.AVAILABLE_BOOKING || status === StatusBooking.CALL_BEFORE_BOOKING);

  // 同一予約での二重計測を防ぐ（戻る操作・完了URLの再訪でコンバージョンが重複計上されるのを避ける）
  const measuredKey = (reservationId: string) => `reservation_measured:${companyCode}:${reservationId}`;
  const isAlreadyMeasured = (reservationId: string) => {
    try {
      return sessionStorage.getItem(measuredKey(reservationId)) !== null;
    } catch (error) {
      return false; // sessionStorage が使用できない環境では従来どおり計測する
    }
  };
  const markMeasured = (reservationId: string) => {
    try {
      sessionStorage.setItem(measuredKey(reservationId), '1');
    } catch (error) {
      // 記録できなくても計測自体は成立するため無視する
    }
  };

  useEffect(() => {
    // アクセス履歴には実際に開かれた画面を記録するため、遷移前に URL を控える
    const accessUrl = `${history?.location?.pathname + history?.location?.search}`;

    // Check accept page
    if (!reservationIdBooking) {
      history.replace({
        search: returnHistoryPath(companyCode, 0, courseCode, mediaCode, gender, serviceCode, trackingCode),
      });
    }

    if (isCompletedReservation && !isAlreadyMeasured(String(reservationIdBooking))) {
      markMeasured(String(reservationIdBooking));
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: status === StatusBooking.AVAILABLE_BOOKING ? 'reservation_thankyou_view' : 'reservation_inquiry_thankyou_view',
        reservation_type: status === StatusBooking.AVAILABLE_BOOKING ? 'IMMEDIATE' : 'REQUEST',
        reservation_company_code: companyCode,
        reservation_org_id: chosenStore || '',
        reservation_org_name: orgNameBooking || '',
        reservation_id: reservationIdBooking || '',
        reservation_status: status || '',
        ecommerce: {
          transaction_id: transactionIdBooking || '',
          userTypes: '初診',
          items: [{ item_name: contractCourseNameBooking || '' }],
        },
      });
    }
    // Save history access screen
    try {
      setLoading(true);
      const paramsPost = { accessUrl, companyCode, staffId: '' };
      accessHistoryApi(paramsPost);
    } catch (error) {
      handleError({ error, enqueueSnackbar });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (companyCode === 'ci_zlabo') {
      // 予約が成立していないとき（直接アクセス・リロード）は親サイトへ完了通知を送らない。
      // reservation_type は即時予約と問い合わせを区別するための追加項目（page は従来どおり thanks）。
      if (isCompletedReservation) {
        window.parent.postMessage({
          event: 'page_view',
          path: location?.pathname || '',
          campaign: '',
          page: 'thanks',
          reservation_type: status === StatusBooking.AVAILABLE_BOOKING ? 'IMMEDIATE' : 'REQUEST',
          application_number: customerNumberBooking || '',
          course: contractCourseNameBooking || '',
          shop: orgNameBooking || '',
          birthday: `${moment(birthDateBooking || '').format('YYYY年MM月DD日') !== 'Invalid date' ? moment(birthDateBooking).format('YYYY年MM月DD日') : ''}`,
          mail: `${String(emailBooking || '')?.substring(0, String(emailBooking || '')?.lastIndexOf('@'))}`, // get retrieve Name from Email Address
        }, 'https://www.ci-z.com');
      }

      updateSize && updateSize();
      window.addEventListener('resize', updateSize);
    }
    return () => {
      // window.removeEventListener('load', updateSize);
      window.removeEventListener('resize', updateSize);
      clearTimeout(timeOutRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ScreenLoader isLoading={isLoading}>
      <div className={classes.root}>
        <SectionHeader title={status === StatusBooking.AVAILABLE_BOOKING ? '受付完了' : 'お問い合わせを受け付けました'} />
        <Box>
          { status === StatusBooking.AVAILABLE_BOOKING ? (
            <>
              { !chosenCourseAcceptRequestReservation ? (
                <p className={classes.content}>
                  ご予約が確定しました。
                  <br />
                  ご来店をお待ちしております。
                </p>
              ) : (
                <p className={classes.content}>
                  仮予約お申込みを受け付けました。
                  <br />
                  24時間以内にお客様がご希望された店舗より、ご予約確認のお電話を差し上げます。
                  <br />
                  お電話が繋がらない場合は、誠に申し訳ございませんがご予約はキャンセルとなりますのであらかじめご了承願います。
                </p>
              )}
            </>
          ) : (
            <>
              <p className={classes.content}>
                24時間以内にお客様がご希望された店舗より、お電話を差し上げます。
                <br />
                予約はリアルタイムで変動していきますので、空き状況に合わせてご案内させて頂きます。
              </p>
            </>
          )}
          <div className={classes.homeBtn}>
            <button type="button" onClick={() => handleBackHome()}>
              HOMEへ
            </button>
          </div>
        </Box>
      </div>
    </ScreenLoader>
  );
};


export default ReceptionCompleted;
